import axios from "axios";
import userInfo from "../function/userInfo";
import i18n from "../locales/i18n";
import { NotificationService, UserService } from "./ApiSevice";

let isRefreshing = false;
let failedQueue = [];

const ProcessQueue = () => {
  const token = userInfo.GetAccessToken();
  if (token && failedQueue.length > 0) {
    failedQueue.forEach((prom) => {
      prom.resolve();
    });
  }
  failedQueue = [];
};

const ReSendApiByNewAccessToken = (originalConfig) => {
  const token = userInfo.GetAccessToken();
  originalConfig.headers["Authorization"] = "Bearer " + token;
  return axios(originalConfig)
    .then((resCallback) => {
      return resCallback;
    })
    .catch(() => {
      location.href = `${window.location.origin}/SessionExpire`;
    });
};

const CreateAxiosInstance = (baseURL, upload = false) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": !upload ? "application/json;charset=utf-8" : "multipart/form-data;charset=utf-8",
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = userInfo.GetAccessToken();
      const merchantId = userInfo.MerchantId();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      if (merchantId) {
        config.headers["merchantId"] = `${merchantId}`;
      }
      config.headers["language"] = i18n.locale;
      config.headers["timezoneOffset"] = userInfo.GetTimezoneOffset();

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      const originalConfig = err.config;
      if (err.response && err.response.status === 401) {
        // Access Token was expire
        if (isRefreshing) {
          return new Promise((resolve) => {
            failedQueue.push({ resolve });
          }).then(() => {
            return ReSendApiByNewAccessToken(originalConfig);
          });
        }

        isRefreshing = true;

        return UserService.DoGetNewAccessTokenByRefreshToken()
          .then((resNewAccessTokenByRefreshToken) => {
            if (resNewAccessTokenByRefreshToken.status.IsSuccess()) {
              const keepLogin = userInfo.GetKeepLogin();
              userInfo.SetCookiesAccessToken(resNewAccessTokenByRefreshToken.data.access_token);
              userInfo.SetCookiesRefreshToken(resNewAccessTokenByRefreshToken.data.refresh_token, keepLogin);
              return ReSendApiByNewAccessToken(originalConfig);
            } else {
              location.href = `${window.location.origin}/SessionExpire`;
            }
          })
          .catch((_error) => {
            userInfo.RemoveCookiesToken();
            location.href = `${window.location.origin}/SessionExpire`;
          })
          .finally(() => {
            ProcessQueue();
            isRefreshing = false;
          });
      }

      return Promise.reject(err);
    }
  );
  return instance;
};

const CreateAxiosInstanceForNotificationService = (baseURL) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = userInfo.GetAccessToken();
      const merchantId = userInfo.MerchantId();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      if (merchantId) {
        config.headers["merchantId"] = `${merchantId}`;
      }
      config.headers["language"] = i18n.locale;
      config.headers["timezoneOffset"] = userInfo.GetTimezoneOffset();

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      const originalConfig = err.config;
      if (err.response && err.response.status === 401) {
        // Access Token was expire
        if (isRefreshing) {
          return new Promise((resolve) => {
            failedQueue.push({ resolve });
          }).then(() => {
            return ReSendApiByNewAccessToken(originalConfig);
          });
        }

        isRefreshing = true;

        return NotificationService.DoUpdateAccessToken()
          .then((res) => {
            if (res.status.IsSuccess()) {
              return ReSendApiByNewAccessToken(originalConfig);
            } else {
              location.href = `${window.location.origin}/SessionExpire`;
            }
          })
          .catch((_error) => {
            location.href = `${window.location.origin}/SessionExpire`;
          })
          .finally(() => {
            ProcessQueue();
            isRefreshing = false;
          });
      }

      return Promise.reject(err);
    }
  );
  return instance;
};

const peakAPI = CreateAxiosInstance(process.env.VUE_APP_PeakExportService);
const peakReportAPI = CreateAxiosInstance(process.env.VUE_APP_PeakExportServiceForReport);
const peakRealTimeAPIForUser = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeServiceForUser);
const peakRealTimeAPIForImport = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeServiceForImport);
const peakRealTimeAPI = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeService);
const peakRealTimeForUploadAPI = CreateAxiosInstance(process.env.VUE_APP_PeakRealTimeService, true);
const peakActiveCampaignAPI = CreateAxiosInstance(process.env.VUE_APP_PeakCheckActiveCampaign);
const peakLogAPI = CreateAxiosInstance(process.env.VUE_APP_PeakLogService);
const peakAuthenticationAPI = CreateAxiosInstance(process.env.VUE_APP_PeakAuthenService);
const peakAppConnect = CreateAxiosInstance(process.env.VUE_APP_PeakAppConnectService);
const peakAffiliatesConnect = CreateAxiosInstance(process.env.VUE_APP_PeakAffiliatesService);
const peakPDFLog = CreateAxiosInstance(process.env.VUE_APP_PeakPDFLogService);
const peakNotificationAPI = CreateAxiosInstanceForNotificationService(process.env.VUE_APP_PeakNotification);

export {
  peakAPI,
  peakActiveCampaignAPI,
  peakAffiliatesConnect,
  peakAppConnect,
  peakAuthenticationAPI,
  peakLogAPI,
  peakNotificationAPI,
  peakPDFLog,
  peakRealTimeAPI,
  peakRealTimeAPIForImport,
  peakRealTimeAPIForUser,
  peakRealTimeForUploadAPI,
  peakReportAPI,
};
